import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SiteService } from '../services/site.service';
import { PlatformService } from '../services/platform.service';
import { Site } from '../models/site.model';
import { redirectToLogin } from './redirect-to-login';
import { readSharedLookbookLocationIds } from './shared-lookbook-access';

@Injectable({ providedIn: 'root' })
export class SitePasswordGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private siteService: SiteService,
    private platformService: PlatformService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    // The gate page itself must always load — the guard also covers the root
    // public route now, and redirecting /password to /password would loop.
    if (state.url.split('?')[0].startsWith('/password')) return true;
    // No sessionStorage on the server — SSR re-evaluates the actual gate
    // state below every request instead of trusting a prior "verified" mark
    // (there is no prior request to have set one; each render is stateless).
    if (this.platformService.isBrowser && sessionStorage.getItem('site_access_verified') === 'true') return true;
    // Email onboarding links carry a flow-specific token, but the token is
    // validated by the destination component. Let only those exact routes
    // load for validation; never turn mere token presence into a session-wide
    // site unlock for an unrelated public page.
    if (route.queryParams['token'] && this.isTokenOnboardingRoute(state.url)) return true;
    // Shared lookbook links carry their own unguessable token (and optional
    // lookbook password) and are sent to people with no account. They must
    // open on login-wall sites too (Lowcountry 610, 2026-09-16: a recipient
    // was bounced to Sign In). Never gate them behind the site wall.
    if (this.isSharedTokenRoute(state.url)) return true;
    // A location the visitor reached FROM a shared lookbook this session
    // (Lowcountry 610, 2026-09-16 10:17 PM: the lookbook opened, every
    // location click bounced to Sign In). The shared page records the ids it
    // rendered; only those ids pass, and no session unlock is set.
    if (this.isSharedLookbookLocation(state.url)) return true;
    if (this.authService.isUserLoggedIn() && this.authService.isAdmin()) {
      this.markVerified();
      return true;
    }

    const site = this.siteService.getCurrentSite();
    if (site) return this.evaluateSite(site, state);

    return this.siteService.site$.pipe(
      filter(s => s !== null),
      take(1),
      map(s => this.evaluateSite(s!, state))
    );
  }

  private evaluateSite(site: Site, state: RouterStateSnapshot): boolean | UrlTree {
    if (site.features?.autoShowLoginModal) {
      if (!this.authService.isUserLoggedIn()) {
        if (state.url.startsWith('/login')) return true;
        // Carries returnUrl so a signed-out visit to a deep link on a
        // login-wall tenant comes back here after signing in. See
        // redirect-to-login.ts.
        return redirectToLogin(this.router, state);
      }
      this.markVerified();
      return true;
    }
    if (site.features?.isPasadenaTenant && state.url.startsWith('/list-property')) return true;
    if (site.features?.isPittsburghTenant) {
      // ONLY the location library list page (/locations) is password-protected.
      // Detail pages (/locations/:id), lookbooks, directory, list-property, login, etc. are all public.
      // Root navigation is handled by HomeRedirectComponent which sends site 589 to /directory.
      const urlPath = state.url.split('?')[0];
      if (urlPath === '/locations') {
        this.markRedirectUrl(state.url);
        return this.router.createUrlTree(['/password']);
      }
      return true;
    }
    if (!site.publicPassword) {
      this.markVerified();
      return true;
    }
    this.markRedirectUrl(state.url);
    return this.router.createUrlTree(['/password']);
  }

  /** sessionStorage writes are browser-only bookkeeping for the /password
   *  flow — SSR has no session to persist across (each render is a single
   *  stateless request), so these are no-ops on the server. */
  private markVerified(): void {
    if (!this.platformService.isBrowser) return;
    sessionStorage.setItem('site_access_verified', 'true');
  }

  private markRedirectUrl(url: string): void {
    if (!this.platformService.isBrowser) return;
    sessionStorage.setItem('site_redirect_url', url);
  }

  private isSharedTokenRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    // /lookbook/:id resolves itself to the token link (or shows the staff-link
    // message); it loads nothing without a token or a login, so the wall can
    // let it through the same way it lets the token route through.
    return /^\/(lookbook|directory-lookbook)\/shared\/[^/]+$/.test(path) || /^\/lookbook\/\d+$/.test(path);
  }

  private isSharedLookbookLocation(url: string): boolean {
    if (!this.platformService.isBrowser) return false;
    const m = /^\/locations\/(\d+)$/.exec(url.split('?')[0].split('#')[0]);
    if (!m) return false;
    return readSharedLookbookLocationIds().has(m[1]);
  }

  private isTokenOnboardingRoute(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/incentive'
      || path === '/projects'
      || path === '/permits'
      || path === '/verify-email';
  }
}
